if (!customElements.get('td-images-grid-slider')) {
  class TDImagesGridSlider extends HTMLElement {
    static selectors = {
      wrapper: '.td-images-grid__grid',
      slide: '.td-images-grid__item',
      pagination: '.td-images-grid__pagination',
    };

    constructor() {
      super();
      this.sliderInstance = null;
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
    }

    connectedCallback() {
      if (this.dataset.enableSlider !== 'true') return;

      this.sectionId = this.dataset.sectionId;
      this.sliderWrapper = this.querySelector(TDImagesGridSlider.selectors.wrapper);
      if (!this.sliderWrapper || !window.FoxTheme || !window.FoxTheme.Carousel) return;

      this.initSlider();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    disconnectedCallback() {
      this.destroySlider();
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);

      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    /**
     * Autoplay delay in ms. 0 means the merchant left auto-rotation off, and a reduced-motion
     * preference always wins over the setting.
     */
    get autoplayDelay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

      const delay = parseInt(this.dataset.autoplay, 10);
      return Number.isNaN(delay) ? 0 : delay;
    }

    initSlider() {
      if (this.sliderInstance) return;

      const slides = this.querySelectorAll(TDImagesGridSlider.selectors.slide);
      if (slides.length < 2) return;

      const columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 1;
      const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
      const columnsTablet = Math.min(columnsDesktop, 3);
      const gapMobile = parseInt(this.dataset.gapMobile, 10) || 0;
      const gapDesktop = parseInt(this.dataset.gapDesktop, 10) || 0;

      const sliderOptions = {
        slidesPerView: columnsMobile,
        spaceBetween: gapMobile,
        // Looping needs more logos than the widest breakpoint shows, otherwise Swiper
        // duplicates its way into a stuttering track.
        loop: slides.length > columnsDesktop,
        watchOverflow: true,
        threshold: 2,
        pagination: {
          el: this.querySelector(TDImagesGridSlider.selectors.pagination),
          clickable: true,
        },
        breakpoints: {
          768: {
            slidesPerView: columnsTablet,
            spaceBetween: gapDesktop,
          },
          1024: {
            slidesPerView: columnsDesktop,
            spaceBetween: gapDesktop,
          },
        },
      };

      const modules = [];
      const autoplayDelay = this.autoplayDelay;

      if (autoplayDelay > 0) {
        sliderOptions.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
        modules.push(window.FoxTheme.Swiper.Autoplay);
      }

      this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, modules.length ? modules : null);
      this.sliderInstance.init();

      if (autoplayDelay > 0) {
        // Swiper pauses on hover for us, but keyboard users get no such courtesy.
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
      }
    }

    destroySlider() {
      if (!this.sliderInstance || !this.sliderInstance.slider) return;
      this.sliderInstance.slider.destroy(true, true);
      this.sliderInstance = null;
    }

    handleFocusIn() {
      const slider = this.sliderInstance && this.sliderInstance.slider;
      if (slider && slider.autoplay) slider.autoplay.stop();
    }

    handleFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;

      const slider = this.sliderInstance && this.sliderInstance.slider;
      if (slider && slider.autoplay) slider.autoplay.start();
    }

    handleBlockSelect(event) {
      const slider = this.sliderInstance && this.sliderInstance.slider;
      if (!slider) return;
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;

      const slide = event.target.closest(TDImagesGridSlider.selectors.slide);
      if (!slide) return;

      const index = Number(slide.dataset.index);
      if (Number.isNaN(index)) return;

      if (slider.params.loop && typeof slider.slideToLoop === 'function') {
        slider.slideToLoop(index);
      } else {
        slider.slideTo(index);
      }
    }
  }

  customElements.define('td-images-grid-slider', TDImagesGridSlider);
}
